import { useCallback, useEffect, useRef, useState } from 'react';
import gql from 'graphql-tag';

import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { cfetch } from '@confluence/network';

import type {
	ContentDetailsById,
	RawAction,
	RawSuggestion,
	SpaceSuggestionsResponse,
} from '../__types__/SpaceSuggestion.ts';

export type UseFetchSpaceSuggestionsResult = {
	suggestions: RawSuggestion[];
	isLoading: boolean;
	error: Error | undefined;
	refetch: () => Promise<void>;
};

const buildSpaceSuggestionsUrl = (spaceKey: string): string =>
	`/wiki/rest/internal/1.0/spaces/${spaceKey}/organization/suggestions`;

type ContentTitleNode = {
	id: string | null | undefined;
	title: string | null | undefined;
};

type ContentTitleConnection = {
	nodes?: readonly (ContentTitleNode | null)[] | null;
} | null;

type ContentTitlesQueryData = {
	pageContent?: ContentTitleConnection;
	folderContent?: ContentTitleConnection;
	whiteboardContent?: ContentTitleConnection;
	databaseContent?: ContentTitleConnection;
	embedContent?: ContentTitleConnection;
	blogpostContent?: ContentTitleConnection;
	slideContent?: ContentTitleConnection;
};

type ContentTitlesQueryVariables = {
	ids: number[];
};

const ContentTitlesQuery = gql`
	query useFetchSpaceSuggestionsContentTitlesQuery($ids: [ID!]!) {
		pageContent: content(ids: $ids, type: "page") {
			nodes {
				id
				title
			}
		}
		folderContent: content(ids: $ids, type: "folder") {
			nodes {
				id
				title
			}
		}
		whiteboardContent: content(ids: $ids, type: "whiteboard") {
			nodes {
				id
				title
			}
		}
		databaseContent: content(ids: $ids, type: "database") {
			nodes {
				id
				title
			}
		}
		embedContent: content(ids: $ids, type: "embed") {
			nodes {
				id
				title
			}
		}
		blogpostContent: content(ids: $ids, type: "blogpost") {
			nodes {
				id
				title
			}
		}
		slideContent: content(ids: $ids, type: "slide") {
			nodes {
				id
				title
			}
		}
	}
`;

/**
 * Extracts the content ID referenced by a single action, or `undefined` if the
 * action type does not reference content.
 */
function getContentIdFromAction(action: RawAction): number | undefined {
	switch (action.actionType) {
		case 'MOVE':
		case 'RENAME':
			return action.parameters.contentId;
		case 'CREATE_FOLDER':
			return action.parameters.parentContentId;
		default:
			return undefined;
	}
}

/**
 * Flattens the actions across all suggestions into a single stream, so callers
 * iterate once (linear) rather than looping suggestions-then-actions.
 */
function* iterateSuggestionActions(suggestions: RawSuggestion[]): Generator<RawAction> {
	for (const suggestion of suggestions) {
		yield* suggestion.actions;
	}
}

/**
 * Extracts every content ID referenced by actions across all suggestions,
 * deduplicated. Every referenced id is looked up via AGG; the render layer
 * decides which resolution source ultimately wins (see `buildContentDetails`),
 * so we no longer skip suggestions that carry `titleReferences`.
 */
function collectContentIds(suggestions: RawSuggestion[]): number[] {
	const ids = new Set<number>();
	for (const action of iterateSuggestionActions(suggestions)) {
		const id = getContentIdFromAction(action);
		if (id !== undefined) {
			ids.add(id);
		}
	}
	return Array.from(ids);
}

/**
 * Batch-fetches content titles for the given IDs using Confluence AGG content
 * lookup. Returns a map from content ID to its resolved title and content type.
 * The content type is derived from which typed query bucket the node resolved
 * in (e.g. a node in `databaseContent` is a `database`).
 *
 * IDs that cannot be resolved (e.g. deleted content, aliases, content types
 * outside the queried set) are silently omitted from the returned map —
 * callers should fall back to the raw ID.
 */
async function fetchContentDetails(contentIds: number[]): Promise<ContentDetailsById> {
	if (contentIds.length === 0) {
		return {};
	}

	try {
		const { data, error } = await queryInterop<ContentTitlesQueryData, ContentTitlesQueryVariables>(
			{
				query: ContentTitlesQuery as unknown as Parameters<typeof queryInterop>[0]['query'],
				variables: {
					ids: contentIds,
				},
				fetchPolicy: 'network-only',
				interopApolloAggClient: true,
			},
		);

		if (error) {
			return {};
		}

		// Each connection maps to a known content type; collect every resolved
		// node tagged with the type of the bucket it came from so callers can
		// pick a matching icon. Buckets are collected with separate single-level
		// loops (not nested iteration) to keep resolution linear.
		const details: ContentDetailsById = {};
		collectContentDetails(details, data?.pageContent, 'page');
		collectContentDetails(details, data?.folderContent, 'folder');
		collectContentDetails(details, data?.whiteboardContent, 'whiteboard');
		collectContentDetails(details, data?.databaseContent, 'database');
		collectContentDetails(details, data?.embedContent, 'embed');
		collectContentDetails(details, data?.blogpostContent, 'blogpost');
		collectContentDetails(details, data?.slideContent, 'slide');
		return details;
	} catch {
		// Content resolution is best-effort; fall back to raw IDs on failure.
		return {};
	}
}

/**
 * Adds every resolvable node from a single typed content connection into
 * `details`, tagging each with the connection's `contentType`.
 */
function collectContentDetails(
	details: ContentDetailsById,
	connection: ContentTitleConnection | undefined,
	contentType: string,
): void {
	for (const node of connection?.nodes ?? []) {
		if (node?.id && node.title) {
			details[node.id] = { title: node.title, contentType };
		}
	}
}

/**
 * Attaches the AGG-resolved content details to each suggestion as a
 * supplementary resolution source. The render layer (via `buildContentDetails`)
 * merges this with the suggestion title's inline references and
 * `titleReferences`, so we deliberately do not mutate the action parameters here.
 */
function attachResolvedContent(
	suggestions: RawSuggestion[],
	resolvedContent: ContentDetailsById,
): RawSuggestion[] {
	return suggestions.map((suggestion) => ({ ...suggestion, resolvedContent }));
}

/**
 * Fetches the Space Organizer Inbox suggestions for the given space.
 *
 * Note: the backend endpoint is currently a mock and returns hardcoded data
 * regardless of the `spaceKey` provided. Migrate to a GraphQL query (via
 * `@confluence/apollo-relay-interop`) once the AGG schema is ready — REST is
 * deprecated for new Confluence frontend data fetching.
 *
 * After fetching suggestions, content IDs referenced by actions are
 * batch-resolved to human-readable titles via AGG content lookup.
 *
 * Concurrency: tracks an epoch per call so that a stale response from a
 * previous `spaceKey` cannot overwrite state for the current `spaceKey`.
 */
export const useFetchSpaceSuggestions = (spaceKey: string): UseFetchSpaceSuggestionsResult => {
	const [suggestions, setSuggestions] = useState<RawSuggestion[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [error, setError] = useState<Error | undefined>(undefined);
	const callEpochRef = useRef<number>(0);

	const refetch = useCallback(async () => {
		const epoch = callEpochRef.current + 1;
		callEpochRef.current = epoch;

		setIsLoading(true);
		setError(undefined);

		try {
			const response = await cfetch(buildSpaceSuggestionsUrl(spaceKey), {
				method: 'GET',
			});

			if (!response.ok) {
				throw new Error(
					`Failed to fetch space suggestions: ${response.status} ${response.statusText}`,
				);
			}

			const data: SpaceSuggestionsResponse = await response.json();

			// Discard the response if a newer call has been issued in the meantime.
			if (callEpochRef.current !== epoch) {
				return;
			}

			const rawSuggestions = data.suggestions ?? [];

			// Resolve content IDs to human-readable titles and types (best-effort).
			const contentIds = collectContentIds(rawSuggestions);
			const contentDetails = await fetchContentDetails(contentIds);

			// Re-check epoch after the async content resolution.
			if (callEpochRef.current !== epoch) {
				return;
			}

			setSuggestions(attachResolvedContent(rawSuggestions, contentDetails));
		} catch (err) {
			if (callEpochRef.current !== epoch) {
				return;
			}
			setError(err instanceof Error ? err : new Error(String(err)));
			setSuggestions([]);
		} finally {
			if (callEpochRef.current === epoch) {
				setIsLoading(false);
			}
		}
	}, [spaceKey]);

	useEffect(() => {
		// eslint-disable-next-line @atlassian/perf-linting/no-chain-state-updates
		void refetch();
	}, [refetch]);

	return { suggestions, isLoading, error, refetch };
};
